import { useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { hasAnyRule } from '../../utils/queryBuilderUtils';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import { useRequestState } from './useRequestState';
import { buildQueryPayload, fetchUnlessRefused, isQueryRefusal, QueryRefusal, SaveQueryParams } from './useSaveQuery';

interface SaveAndCountResponse {
    taskId?: string;
    queryId?: number;
    count?: number;
}

export type SaveAndCountResult =
    | { mode: 'async'; taskId: string; queryId?: number }
    | { mode: 'sync'; queryId: number; count: number };

export const useSaveAndCountQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const { loading, error, run } = useRequestState();

    // Avec queryId, recompte cette requête (mise à jour) au lieu d'en créer une nouvelle
    const saveAndCountQuery = useCallback(async (params: SaveQueryParams): Promise<SaveAndCountResult | QueryRefusal | null> => {
        if (!isReady || !hasAnyRule(params.groups)) return null;

        return run(async (): Promise<SaveAndCountResult | QueryRefusal> => {
            const response: SaveAndCountResponse | QueryRefusal | null = await fetchUnlessRefused(() => authFetch('/saveAndCountQuery', {
                method: 'POST',
                body: JSON.stringify(buildQueryPayload(params)),
            }));
            if (isQueryRefusal(response)) return response;

            cache?.invalidate('saved_queries|');

            if (response?.taskId) return { mode: 'async', taskId: response.taskId, queryId: response.queryId };
            if (response?.queryId !== undefined && response.count !== undefined) {
                return { mode: 'sync', queryId: response.queryId, count: response.count };
            }
            throw new Error('Réponse invalide du serveur');
        }, "Erreur lors de l'exécution");
    }, [authFetch, isReady, cache, run]);

    return { saveAndCountQuery, loading, error };
};
